// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  Box,
  DataTable,
  NoData,
  Pagination,
  RowsPerPageType,
  RowsSkeletonLoader,
  Tabs,
  useSidePanel,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useGetCryptoMassPayoutsListByPolling } from 'hooks/queries/MassPayouts/useGetCryptoMassPayoutsListByPolling'
import { useGetMassFiatPayoutsListQuery } from 'hooks/queries/MassPayouts/useGetMassFiatPayoutsList'
import { useWalletsQueryV2 } from 'hooks/queries/useWalletsQueryV2'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { WalletTypes } from 'types/types'
import { walletTypeAtom } from '../atoms/massPayoutAtoms'
import MassPayoutHeader from '../components/MassPayoutHeader'
import { CryptoBatchSidePanel } from '../components/SidePanel/CryptoBatchSidePanel'
import { FiatBatchSidePanel } from '../components/SidePanel/FiatBatchSidePanel'
import { useMassPayoutsBatchesTableData } from '../hooks/useMassPayoutsBatchesTableData'
import { FiatPayout, MassPayoutButtonGroup, MassPayoutCryptoBatch } from '../types'

interface MassPayoutBatchViewProps {
  isFetching: boolean
}

export const MassPayoutBatchView = ({ isFetching }: MassPayoutBatchViewProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<RowsPerPageType>(10)
  const [walletType, setWalletType] = useAtom(walletTypeAtom)
  const { tab } = queryString.parse(window.location.search) as { tab?: WalletTypes }
  const [activeTab, setActiveTab] = useState(tab ?? WalletTypes.CRYPTO)

  const { data: cryptoBatchListPolling, isPending: isLoadingMassCryptoPayoutList } =
    useGetCryptoMassPayoutsListByPolling(activeTab === WalletTypes.CRYPTO)

  const { data: wallets, isLoading: isLoadingWallets } = useWalletsQueryV2({
    walletType: WalletTypes.FIAT,
    max: 1000,
  })

  const {
    data: { content: massFiatPayoutList, pagination: fiatListPagination } = {},
    refetch: fetchMassFiatPayoutList,
    isFetching: isLoadingMassFiatPayoutList,
  } = useGetMassFiatPayoutsListQuery({ page, size: pageSize, enabled: false })

  const { paging, showPagination } = usePaginationPagingPersist(fiatListPagination)

  useEffect(() => {
    fetchMassFiatPayoutList()
  }, [page, pageSize])

  useEffect(() => {
    setWalletType(walletType)
    setActiveTab(walletType)
    window.history.pushState({}, '', `?tab=${walletType}`)
  }, [])

  const isLoadingBatches = useMemo(() => {
    return activeTab === WalletTypes.FIAT
      ? isLoadingMassFiatPayoutList || isFetching
      : isLoadingMassCryptoPayoutList || isFetching
  }, [isLoadingMassFiatPayoutList, isLoadingMassCryptoPayoutList, activeTab, isFetching])

  const {
    isSidePanelOpen: isFiatSidePanelOpen,
    sidePanelData: fiatSidePanelData,
    paging: fiatPaging,
    setIsSidePanelOpen: fiatSetIsSidePanelOpen,
    updateSidePanel: fiatUpdateSidePanel,
  } = useSidePanel<FiatPayout>(massFiatPayoutList)

  const {
    isSidePanelOpen: isCryptoSidePanelOpen,
    sidePanelData: cryptoSidePanelData,
    paging: cryptoPaging,
    setIsSidePanelOpen: cryptoSetIsSidePanelOpen,
    updateSidePanel: cryptoUpdateSidePanel,
  } = useSidePanel(cryptoBatchListPolling)

  const onOpenFiatSidePanel = (payoutIndex: number) => {
    fiatUpdateSidePanel(payoutIndex)
    fiatSetIsSidePanelOpen(true)
  }

  const onOpenCryptoSidePanel = (payoutIndex: number) => {
    cryptoUpdateSidePanel(payoutIndex)
    cryptoSetIsSidePanelOpen(true)
  }

  const goToCryptoMassPayoutBatch = (batch: MassPayoutCryptoBatch, activeTab: string) => {
    void navigate(
      `${PathNames.MASS_PAYOUTS}/payout-details/${batch.externalId}?batchType=${activeTab}`
    )
  }

  const goToFiatMassPayoutBatch = (batch: FiatPayout) => {
    void navigate(
      `${PathNames.MASS_PAYOUTS}/payout-details/${batch.reference}?batchType=${activeTab}`
    )
  }

  const { columns, data: tableData } = useMassPayoutsBatchesTableData({
    activeTab,
    // fiat
    fiatData: massFiatPayoutList,
    onOpenFiatSidePanel,
    goToFiatMassPayoutBatch,
    // crypto
    isCrypto: activeTab === WalletTypes.CRYPTO,
    cryptoData: cryptoBatchListPolling,
    onOpenCryptoSidePanel,
    goToCryptoMassPayoutBatch,
  })

  const switchToTab = (tab: WalletTypes) => {
    // add query param to current URL without causing page reload
    window.history.pushState({}, '', `?tab=${tab}`)
    setWalletType(tab)
    setActiveTab(tab)
  }

  const tabs = [
    {
      id: WalletTypes.CRYPTO,
      header: t('wallets.crypto'),
      content: null,
      onClick: () => switchToTab(WalletTypes.CRYPTO),
      testId: 'crypto-wallets-tab',
    },
    {
      id: WalletTypes.FIAT,
      header: t('wallets.fiat'),
      content: null,
      onClick: () => switchToTab(WalletTypes.FIAT),
      testId: 'fiat-wallets-tab',
    },
  ]

  const isListEmpty = tableData?.length === 0
  const showTable = !isListEmpty

  const handlePaginationChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage: RowsPerPageType) => {
    setPageSize(newRowsPerPage)
    setPage(0)

    queryClient.invalidateQueries({
      queryKey: ['massFiatPayoutsList'],
    })
  }

  return (
    <Box className="w-full">
      <MassPayoutHeader buttonGroup={MassPayoutButtonGroup?.BATCH_LIST} />

      <Tabs tabs={tabs} defaultTabId={walletType} />

      {isFetching ? (
        <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
      ) : (
        <>
          {!showTable && (
            <NoData
              dataName="mass payouts"
              description={t('massPayouts.massPayoutsEmptyDescription')}
            />
          )}
          {showTable && (
            <Box>
              <DataTable name="mass-payouts-table" headers={columns} data={tableData} />
            </Box>
          )}
        </>
      )}

      {activeTab === WalletTypes.FIAT ? (
        <>
          <FiatBatchSidePanel
            isOpen={isFiatSidePanelOpen}
            massFiatPayoutDetails={fiatSidePanelData}
            pageMassPayout={fiatPaging}
            onClose={() => fiatSetIsSidePanelOpen(false)}
            wallets={wallets?.data}
            isLoadingWallets={isLoadingWallets}
          />
          {!!paging && !showPagination && (
            <Box className={`${isLoadingMassFiatPayoutList || showPagination ? 'opacity-25' : ''}`}>
              <Pagination
                testId="mass-payouts-pagination"
                page={page}
                rowsPerPage={pageSize}
                total={paging?.total ?? 0}
                onChangePage={handlePaginationChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
              />
            </Box>
          )}
        </>
      ) : (
        <CryptoBatchSidePanel
          isOpen={isCryptoSidePanelOpen}
          massPayoutDetails={cryptoSidePanelData as MassPayoutCryptoBatch}
          pageMassPayout={cryptoPaging}
          onClose={() => cryptoSetIsSidePanelOpen(false)}
        />
      )}
    </Box>
  )
}
